import { useCallback, useEffect, useState } from "react";
import { API, api } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MonthCalendar } from "@/components/MonthCalendar";
import { fmtBRL, fmtDataBR } from "@/components/ReservaFormDialog";
import { Ban, CalendarDays, CheckCircle2, FileDown, Percent, Printer } from "lucide-react";

export default function ReservasReportPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [areas, setAreas] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", area_id: "todas" });
  const [cal, setCal] = useState(() => { const n = new Date(); return { ano: n.getFullYear(), mes: n.getMonth() }; });

  const load = useCallback(() => {
    setError(false);
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.area_id !== "todas") params.area_id = filters.area_id;
    api.get("/tenant/reports/reservas", { params })
      .then(({ data }) => setData(data))
      .catch(() => { setError(true); setData({ metrics: null, por_espaco: [], top_moradores: [], ocupacao: {} }); });
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/tenant/areas").then(({ data }) => setAreas(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!filters.date_from) return;
    const [y, m] = filters.date_from.split("-").map(Number);
    if (y && m) setCal({ ano: y, mes: m - 1 });
  }, [filters.date_from]);

  const pdfUrl = () => {
    const qs = new URLSearchParams();
    if (filters.date_from) qs.set("date_from", filters.date_from);
    if (filters.date_to) qs.set("date_to", filters.date_to);
    if (filters.area_id !== "todas") qs.set("area_id", filters.area_id);
    const s = qs.toString();
    return `${API}/tenant/reports/reservas.pdf${s ? `?${s}` : ""}`;
  };

  if (!data) return <Spinner />;
  const { metrics, por_espaco, top_moradores, ocupacao } = data;

  const dayState = (ds) => (ocupacao?.[ds] > 0 ? "ocupado" : "livre");
  const navigate = (delta) => setCal((c) => {
    const d = new Date(c.ano, c.mes + delta, 1);
    return { ano: d.getFullYear(), mes: d.getMonth() };
  });

  const hojeLocal = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  };
  const setHoje = () => setFilters((f) => ({ ...f, date_from: hojeLocal(), date_to: hojeLocal() }));
  const selectDia = (ds) => setFilters((f) => ({ ...f, date_from: ds, date_to: ds }));

  if (error) {
    return (
      <div data-testid="reservas-report-page">
        <PageHeader title="Relatório de Reservas" subtitle="Ocupação e uso das áreas comuns" />
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-8 text-center" data-testid="reservas-report-error">
          <p className="text-sm text-rose-400 mb-4">Não foi possível carregar o relatório. Tente novamente.</p>
          <Button data-testid="reservas-report-retry" onClick={load}
            className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reservas-report-page">
      <PageHeader title="Relatório de Reservas" subtitle="Resumo executivo, uso por espaço e ocupação mensal das áreas comuns">
        <Button data-testid="reservas-report-print" variant="outline" onClick={() => window.print()}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
          <Printer className="h-4 w-4 mr-1.5" /> Imprimir
        </Button>
        <Button data-testid="reservas-report-pdf" onClick={() => window.open(pdfUrl(), "_blank")}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <FileDown className="h-4 w-4 mr-1.5" /> Exportar PDF
        </Button>
      </PageHeader>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3 mb-6 flex flex-wrap items-end gap-3" data-testid="reservas-report-filters">
        <div>
          <Label className="text-xs text-zinc-400">Período de</Label>
          <Input type="date" data-testid="reservas-report-from" value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">até</Label>
          <Input type="date" data-testid="reservas-report-to" value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
        </div>
        <Button type="button" data-testid="reservas-report-hoje" variant="outline" onClick={setHoje}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
          Hoje
        </Button>
        <div>
          <Label className="text-xs text-zinc-400">Espaço</Label>
          <Select value={filters.area_id} onValueChange={(v) => setFilters({ ...filters, area_id: v })}>
            <SelectTrigger data-testid="reservas-report-area" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              <SelectItem data-testid="reservas-report-area-todas" value="todas">Todos</SelectItem>
              {areas.map((a) => (
                <SelectItem key={a.id} data-testid={`reservas-report-area-${a.id}`} value={a.id}>{a.code} — {a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="reservas-report-metrics">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="metric-total">
            <div className="h-8 w-8 rounded-md bg-indigo-400/10 flex items-center justify-center mb-3"><CalendarDays className="h-4 w-4 text-indigo-400" /></div>
            <p className="text-2xl font-semibold text-zinc-100">{metrics.total}</p>
            <p className="text-xs text-zinc-500 mt-1">Total de reservas</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="metric-ativas">
            <div className="h-8 w-8 rounded-md bg-emerald-400/10 flex items-center justify-center mb-3"><CheckCircle2 className="h-4 w-4 text-emerald-400" /></div>
            <p className="text-2xl font-semibold text-zinc-100">{metrics.ativas}</p>
            <p className="text-xs text-zinc-500 mt-1">Confirmadas / concluídas</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="metric-canceladas">
            <div className="h-8 w-8 rounded-md bg-amber-400/10 flex items-center justify-center mb-3"><Ban className="h-4 w-4 text-amber-400" /></div>
            <p className="text-2xl font-semibold text-zinc-100">{metrics.canceladas}</p>
            <p className="text-xs text-zinc-500 mt-1">Canceladas</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="metric-taxa">
            <div className="h-8 w-8 rounded-md bg-rose-400/10 flex items-center justify-center mb-3"><Percent className="h-4 w-4 text-rose-400" /></div>
            <p className="text-2xl font-semibold text-zinc-100">{metrics.taxa_cancelamento}%</p>
            <p className="text-xs text-zinc-500 mt-1">Taxa de cancelamento</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="reservas-por-espaco">
          <p className="text-sm font-medium text-zinc-100 mb-3">Resumo por espaço</p>
          {por_espaco.length === 0 ? (
            <p className="text-xs text-zinc-500">Nenhuma reserva no período.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-left">
                  <th className="py-2 text-xs font-medium text-zinc-400">Espaço</th>
                  <th className="py-2 text-xs font-medium text-zinc-400 text-right">Reservas</th>
                  <th className="py-2 text-xs font-medium text-zinc-400 text-right">Horas</th>
                  <th className="py-2 text-xs font-medium text-zinc-400 text-right">Receita</th>
                </tr>
              </thead>
              <tbody>
                {por_espaco.map((e) => (
                  <tr key={e.espaco} data-testid={`por-espaco-${e.espaco}`} className="border-b border-zinc-800/60">
                    <td className="py-2 text-zinc-200 text-xs max-w-40 truncate">{e.espaco}</td>
                    <td className="py-2 text-zinc-400 text-xs text-right">{e.reservas}</td>
                    <td className="py-2 text-zinc-400 text-xs text-right">{e.horas.toFixed(1)}h</td>
                    <td className="py-2 text-zinc-400 text-xs text-right">{fmtBRL(e.receita)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="reservas-ocupacao">
            <p className="text-sm font-medium text-zinc-100 mb-3">Ocupação mensal <span className="text-[11px] text-zinc-500 font-normal">— clique em um dia para filtrar o relatório</span></p>
            <MonthCalendar ano={cal.ano} mes={cal.mes} onNavigate={navigate} dayState={dayState}
              onSelect={selectDia} selected={filters.date_from === filters.date_to ? filters.date_from : null} />
            <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
              {Object.entries(ocupacao || {})
                .filter(([ds]) => {
                  const [y, m] = ds.split("-").map(Number);
                  return y === cal.ano && m === cal.mes + 1;
                })
                .sort()
                .map(([ds, horas]) => (
                  <p key={ds} data-testid={`ocupacao-${ds}`} className="text-[11px] text-zinc-500">
                    {fmtDataBR(ds)} — <span className="text-zinc-300">{horas}h reservadas</span>
                  </p>
                ))}
            </div>
          </div>
          {top_moradores.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="reservas-top-moradores">
              <p className="text-sm font-medium text-zinc-100 mb-3">Moradores com mais reservas</p>
              <div className="space-y-1.5">
                {top_moradores.map((m, i) => (
                  <div key={m.morador} data-testid={`top-morador-${i}`} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-300 truncate max-w-56">{m.morador} <span className="text-zinc-500">({m.unidade})</span></span>
                    <span className="text-zinc-500">{m.reservas} reserva(s) · {m.horas.toFixed(1)}h</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
